import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { ToggleButton } from '../../../components/ToggleButton';
import { ToggleButtonGroup } from '../../../components/ToggleButtonGroup';
import { Toggles } from '../../../components/ToggleButtonGroup/ToggleButtonGroup';
import { StepProps } from '../../../types';
import { useBriefingState } from '../BriefingState';

export const StepTargetConsumer: FC<StepProps> = ({ mandatoryFields }) => {
  const [
    { targetConsumerAge, targetConsumerGender, branch },
    { setTargetConsumerAge, setTargetConsumerGender },
  ] = useBriefingState();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [initialAgeSelection] = useState(targetConsumerAge as Toggles);
  const [initialGenderSelection] = useState(targetConsumerGender);

  const ageOptions = [
    '0-3',
    '3-13',
    '13-18',
    '18-25',
    '25-35',
    '35-50',
    '50-65',
    '65+',
    `${t('topics:target-consumer:buttons:allAges')}`,
  ];
  const genderOptions = Object.values<string>(
    t('topics:target-consumer:buttons:genders', { returnObjects: true }),
  );

  const onChangeAge = useCallback(
    (toggles: Toggles) => dispatch(setTargetConsumerAge(toggles as string[])),
    [dispatch, setTargetConsumerAge],
  );
  const onChangeGender = useCallback(
    (toggles: Toggles) =>
      dispatch(setTargetConsumerGender(toggles[0] as string)),
    [dispatch, setTargetConsumerGender],
  );

  const targetConsumerAgeIsAllAges: boolean | undefined = useMemo(
    () => targetConsumerAge?.includes('All ages'),
    [targetConsumerAge],
  );

  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading
        level="h1"
        text={t([
          `topics:target-consumer:heading:${branch}`,
          `topics:target-consumer:heading:general`,
        ])}
        margin={0}
        optionalField={mandatoryFields['target-consumer']?.length === 0}
      />
      <TemplateFlexGridContainer.BriefingStepBody>
        <TemplateFlexGridContainer.BriefingStepSection>
          <div className="w-full">
            <Heading
              level="h3"
              margin={0}
              text={t('topics:target-consumer:age')}
            />
            <p className="text-gray-600 text-2xs">
              {t('labels:multiSelectHint')}
            </p>
          </div>
          {/* This container div is here to stop weird Safari bug: AZEELAB-111 */}
          <div>
            <ToggleButtonGroup
              initialToggles={initialAgeSelection}
              multi={targetConsumerAgeIsAllAges ? false : true}
              onChange={onChangeAge}
            >
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-8">
                {ageOptions.map((ageGroup, i) => (
                  <ToggleButton
                    text={ageGroup}
                    value={ageGroup}
                    key={`age_${i}`}
                    on={true}
                  />
                ))}
              </div>
            </ToggleButtonGroup>
          </div>
        </TemplateFlexGridContainer.BriefingStepSection>
        <TemplateFlexGridContainer.BriefingStepSection>
          <Heading
            level="h3"
            margin={0}
            text={t('topics:target-consumer:gender')}
          />
          {/* This container div is here to stop weird Safari bug: AZEELAB-111 */}
          <div>
            <ToggleButtonGroup
              {...(initialGenderSelection &&
              initialGenderSelection === targetConsumerGender
                ? { initialToggles: [initialGenderSelection] }
                : {})}
              // initialToggles={initialGenderSelection ? [initialGenderSelection] : undefined}
              onChange={onChangeGender}
            >
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                {genderOptions.map((gender, i) => (
                  <ToggleButton
                    text={gender}
                    value={gender}
                    key={`gender_${i}`}
                    on={true}
                  />
                ))}
              </div>
            </ToggleButtonGroup>
          </div>
        </TemplateFlexGridContainer.BriefingStepSection>
      </TemplateFlexGridContainer.BriefingStepBody>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
